import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { SITE } from '@/lib/site';

/** Composer shown only when the site owner is logged in. Publishes kind 1 notes. */
export function NoteComposer() {
  const { user } = useCurrentUser();
  const author = useAuthor(SITE.ownerPubkey);
  const metadata = author.data?.metadata;
  const { mutate: createEvent, isPending } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [content, setContent] = useState('');

  // Only the site owner can post to this feed.
  if (!user || user.pubkey !== SITE.ownerPubkey) {
    return null;
  }

  const trimmed = content.trim();
  const initials = SITE.name.split(' ').map((w) => w[0]).join('');

  const handleSubmit = () => {
    if (!trimmed) return;

    createEvent(
      { kind: 1, content: trimmed },
      {
        onSuccess: () => {
          setContent('');
          toast({ title: 'Note published', description: 'Your note is live on Nostr.' });
          queryClient.invalidateQueries({ queryKey: ['owner-notes'] });
        },
        onError: (error) => {
          toast({
            title: 'Failed to publish',
            description: error.message || 'Please try again.',
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <div className="rounded-xl border border-bitcoin/30 bg-card p-5 shadow-sm sm:p-6">
      <div className="flex gap-3">
        <Avatar className="size-10 shrink-0 border border-border">
          <AvatarImage src={metadata?.picture} alt={SITE.name} />
          <AvatarFallback className="bg-gradient-to-br from-bitcoin to-nostr text-xs font-bold text-white">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 space-y-3">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share an update with the world..."
            rows={3}
            className="resize-none border-border/60 bg-transparent text-base focus-visible:ring-bitcoin/50"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && trimmed && !isPending) {
                handleSubmit();
              }
            }}
          />

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Posting to Nostr as <span className="font-medium text-foreground">{metadata?.display_name || metadata?.name || 'you'}</span>
              <span className="hidden sm:inline"> · ⌘/Ctrl + Enter to send</span>
            </p>
            <Button
              onClick={handleSubmit}
              disabled={!trimmed || isPending}
              size="sm"
              className="rounded-full font-semibold"
            >
              {isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              {isPending ? 'Publishing...' : 'Publish'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
